// FONTLARIM — kullanicinin yukledigi fontlar (Creative Fabrica vb.): toplu
// yukleme (dosya ya da zip), ornek isimle onizleme, silme. Yuklenen fontlar
// sablon editorunde ve ChatGPT'de (list_fonts / font_specimen) gorunur.

import { useEffect, useRef, useState } from "react";
import { X, Upload, Loader2, Trash2, Type, Check, AlertCircle } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { unzip } from "../../lib/unzip";

export interface UserFontRow { id?: string; family: string; style?: string; noteEn?: string; url?: string; color?: boolean; source?: "user" | "google" }

/** Renkli font onizlemesi: Chrome OpenType-SVG renklerini gostermez, gorsel sunucuda basilir */
function ColorSpecimen({ id, text, family }: { id: string; text: string; family: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    const t = setTimeout(() => {
      fetch(`${getBackendUrl()}/api/analyses/customization/fonts/${id}/specimen?text=${encodeURIComponent(text)}`, { headers: authHeaders() })
        .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
        .then((b) => { if (!alive) return; url = URL.createObjectURL(b); setSrc(url); setFailed(false); })
        .catch(() => alive && setFailed(true));
    }, 400);
    return () => { alive = false; clearTimeout(t); if (url) URL.revokeObjectURL(url); };
  }, [id, text]);
  if (failed) return <p className="mt-1 text-xs text-red-600">Önizleme oluşturulamadı.</p>;
  if (!src) return <div className="mt-1 h-40 animate-pulse rounded-lg bg-gray-100" />;
  return <img src={src} alt={`${family} önizlemesi`} className="mt-1 w-full max-w-2xl" />;
}

type Status = { name: string; state: "wait" | "busy" | "ok" | "dup" | "err"; msg?: string };

const FONT_RE = /\.(otf|ttf|woff)$/i;
const RANK: Record<string, number> = { otf: 0, ttf: 1, woff: 2 };

async function call<T>(path: string, method: string, body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/analyses/customization/fonts${path}`, { method, headers: authHeaders(), body: body ? JSON.stringify(body) : undefined });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `İstek başarısız (${r.status})`);
  return j.data as T;
}

const toDataUrl = (data: Uint8Array<ArrayBuffer>) => new Promise<string>((ok, fail) => {
  const fr = new FileReader();
  fr.onload = () => ok(String(fr.result));
  fr.onerror = () => fail(new Error("Dosya okunamadı"));
  fr.readAsDataURL(new Blob([data], { type: "application/octet-stream" }));
});

/** Secilen dosyalar + zip icerikleri -> yuklenecek font dosyalari. Ayni font
 *  birden cok bicimde geldiyse (OTF + TTF + WOFF) yalniz biri alinir. */
async function collect(files: File[]): Promise<Array<{ name: string; data: Uint8Array<ArrayBuffer> }>> {
  const all: Array<{ name: string; data: Uint8Array<ArrayBuffer> }> = [];
  for (const f of files) {
    if (/\.zip$/i.test(f.name)) {
      const entries = await unzip(await f.arrayBuffer(), (n) => FONT_RE.test(n) && !n.startsWith("__MACOSX/") && !n.split("/").pop()!.startsWith("._"));
      all.push(...entries.map((e) => ({ name: e.name.split("/").pop()!, data: e.data })));
    } else if (FONT_RE.test(f.name)) {
      all.push({ name: f.name, data: new Uint8Array(await f.arrayBuffer()) });
    }
  }
  const best = new Map<string, { name: string; data: Uint8Array<ArrayBuffer> }>();
  for (const x of all) {
    const stem = x.name.replace(FONT_RE, "").toLowerCase();
    const cur = best.get(stem);
    const ext = (n: string) => n.split(".").pop()!.toLowerCase();
    if (!cur || RANK[ext(x.name)] < RANK[ext(cur.name)]) best.set(stem, x);
  }
  return [...best.values()];
}

export function FontLibraryModal({ fonts, onClose, onChanged }: { fonts: UserFontRow[]; onClose: () => void; onChanged: () => void }) {
  const own = fonts.filter((f) => f.source === "user");
  const fileRef = useRef<HTMLInputElement>(null);
  const [sample, setSample] = useState("Olivia Grace");
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmId, setConfirmId] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const upload = async (files: File[]) => {
    setError("");
    setBusy(true);
    try {
      const list = await collect(files);
      if (!list.length) { setError("Seçilen dosyalarda .otf, .ttf ya da .woff font bulunamadı."); return; }
      setStatuses(list.map((x) => ({ name: x.name, state: "wait" })));
      // Sirayla: sunucu her font icin AI stil tanimi yapar
      for (const [i, x] of list.entries()) {
        const set = (s: Partial<Status>) => setStatuses((prev) => prev.map((p, j) => (j === i ? { ...p, ...s } : p)));
        set({ state: "busy" });
        try {
          const r = await call<{ family: string; duplicate?: boolean }>("", "POST", { file: await toDataUrl(x.data), fileName: x.name });
          set({ state: r.duplicate ? "dup" : "ok", msg: r.family });
        } catch (e) {
          set({ state: "err", msg: e instanceof Error ? e.message : String(e) });
        }
      }
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setError("");
    try { await call(`/${id}`, "DELETE"); setConfirmId(null); onChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const done = statuses.filter((s) => s.state === "ok" || s.state === "dup" || s.state === "err").length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !busy && onClose()}>
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900"><Type className="h-4 w-4 text-indigo-600" />Fontlarım</h2>
            <p className="mt-0.5 text-xs text-gray-500">Yüklediğin fontlar şablon editöründe ve ChatGPT'de kullanılabilir. Zip'i açmadan yükleyebilirsin.</p>
          </div>
          <button onClick={onClose} disabled={busy} aria-label="Kapat" title="Kapat" className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-40">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto p-5">
          <div className="flex flex-wrap items-end gap-3">
            <input ref={fileRef} type="file" multiple accept=".ttf,.otf,.woff,.zip" className="hidden"
              onChange={(e) => { const f = Array.from(e.target.files ?? []); e.target.value = ""; if (f.length) void upload(f); }} />
            <button onClick={() => fileRef.current?.click()} disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Font yükle
            </button>
            <label className="min-w-[200px] flex-1">
              <span className="mb-1 block text-xs font-medium text-gray-700">Örnek yazı</span>
              <input value={sample} onChange={(e) => setSample(e.target.value)} placeholder="Olivia Grace"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
            </label>
            <span className="text-xs text-gray-500">{own.length} font</span>
          </div>
          <p className="text-xs text-gray-500">.otf, .ttf, .woff ya da bunları içeren .zip. Aynı font birden çok biçimde varsa yalnız biri yüklenir. Renkli fontlar baskıda kendi renkleriyle çıkar; bu fontlara yazı rengi uygulanmaz.</p>

          {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

          {statuses.length > 0 && (
            <div className="rounded-xl border border-gray-200 p-3">
              <p className="mb-2 text-xs font-medium text-gray-700">Yükleme: {done} / {statuses.length}</p>
              <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
                {statuses.map((s, i) => (
                  <li key={i} className="flex items-center gap-2">
                    {s.state === "busy" ? <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-600" />
                      : s.state === "ok" ? <Check className="h-3.5 w-3.5 text-emerald-600" />
                      : s.state === "dup" ? <Check className="h-3.5 w-3.5 text-gray-400" />
                      : s.state === "err" ? <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                      : <span className="h-3.5 w-3.5" />}
                    <span className="truncate text-gray-700">{s.name}</span>
                    {s.state === "ok" && <span className="text-emerald-700">eklendi: {s.msg}</span>}
                    {s.state === "dup" && <span className="text-gray-500">zaten yüklü: {s.msg}</span>}
                    {s.state === "err" && <span className="text-red-600">{s.msg}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {own.length === 0 ? (
            <div className="rounded-xl border border-gray-200 p-8 text-center">
              <Type className="mx-auto h-8 w-8 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">Henüz yüklenmiş font yok. Creative Fabrica'dan indirdiğin zip'i doğrudan yükleyebilirsin.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
              {own.map((f) => (
                <li key={f.id ?? f.family} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="font-medium text-gray-700">{f.family}</span>
                      {f.style && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">{f.style}</span>}
                      {f.color && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700" title="Renkleri baskıda korunur; şablonda yazı rengi uygulanmaz">renkli</span>}
                    </div>
                    {f.color && f.id ? <ColorSpecimen id={f.id} text={sample || f.family} family={f.family} /> : (
                      <>
                        <p className="break-words text-5xl leading-snug text-gray-900" style={{ fontFamily: `'${f.family.replace(/'/g, "")}'` }}>{sample || f.family}</p>
                        <p className="mt-1 break-words text-xl leading-relaxed text-gray-700" style={{ fontFamily: `'${f.family.replace(/'/g, "")}'` }}>
                          ABCDEFGHIJKLMNOPQRSTUVWXYZ<br />abcdefghijklmnopqrstuvwxyz<br />0123456789 &amp; ! ? . , ' -
                        </p>
                      </>
                    )}
                  </div>
                  {f.id && (confirmId === f.id ? (
                    <div className="flex items-center gap-1">
                      <button onClick={() => void remove(f.id!)} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700">Sil</button>
                      <button onClick={() => setConfirmId(null)} className="rounded-lg px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100">Vazgeç</button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmId(f.id!)} aria-label={`${f.family} fontunu sil`} title="Sil"
                      className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
